'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, getCurrentUser, getCurrentUserWithToken, login as loginApi, googleLogin as googleLoginApi, verify2FA as verify2FAApi, signup as signupApi, logout as logoutApi } from '@/lib/auth-client';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  token: string | null;
  login: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
    code?: string;
    userId?: string;
    bannedUntil?: string | null;
    bannedReason?: string;
    requires2FA?: boolean;
    challengeToken?: string;
    requires2FASetup?: boolean;
  }>;
  googleLogin: (credential: string) => Promise<{
    success: boolean;
    error?: string;
    code?: string;
    requires2FA?: boolean;
    challengeToken?: string;
    requires2FASetup?: boolean;
    bannedUntil?: string | null;
    bannedReason?: string;
  }>;
  verify2FA: (challengeToken: string, code: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  signup: (email: string, displayName: string, password: string) => Promise<{ 
    success: boolean; 
    error?: string; 
    user?: User; 
    message?: string;
    code?: string;
  }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const response = await getCurrentUserWithToken();
      if (response.success && response.user) {
        setUser(response.user);
        if (response.token) {
          setToken(response.token);
        }
      } else {
        setUser(null);
        setToken(null);
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const result = await loginApi(email, password);
    
    if (result.success && result.user) {
      setUser(result.user);
      if (result.token) {
        setToken(result.token);
      }
      return { success: true };
    }

    // Admin 2FA gate: backend returns success:true WITHOUT a user/token, asking
    // for a TOTP code (or first-time enrollment). Surface this so the modal can
    // show the code step instead of treating it as a failed login.
    if (result.success && result.requires2FA) {
      return {
        success: false,
        requires2FA: true,
        challengeToken: result.challengeToken,
      };
    }
    if (result.success && result.requires2FASetup) {
      return {
        success: false,
        requires2FASetup: true,
      };
    }

    // Check for fingerprint banned error (403 status)
    if ((result as any).code === 'FINGERPRINT_BANNED') {
      // Clear any stored tokens/user data
      setUser(null);
      setToken(null);
      
      return { 
        success: false, 
        error: result.error || 'Login is not allowed from this device',
        code: 'FINGERPRINT_BANNED'
      };
    }
    
    // Check for account banned error (403 status)
    if ((result as any).code === 'ACCOUNT_BANNED') {
      // Clear any stored tokens/user data
      setUser(null);
      setToken(null);
      
      return { 
        success: false, 
        error: result.error || 'Account is banned',
        code: 'ACCOUNT_BANNED',
        bannedUntil: (result as any).bannedUntil,
        bannedReason: (result as any).bannedReason
      };
    }
    
    // Check for email verification error
    if ((result as any).code === 'EMAIL_NOT_VERIFIED') {
      return { 
        success: false, 
        error: result.error || 'Email verification required',
        code: 'EMAIL_NOT_VERIFIED',
        userId: (result as any).userId
      };
    }
    
    return { success: false, error: result.error || 'Login failed' };
  };

  const googleLogin = async (credential: string) => {
    const result = await googleLoginApi(credential);

    if (result.success && result.user) {
      setUser(result.user);
      if (result.token) {
        setToken(result.token);
      }
      return { success: true };
    }

    // 2FA gate on a linked account (same as password login).
    if (result.success && result.requires2FA) {
      return { success: false, requires2FA: true, challengeToken: result.challengeToken };
    }
    if (result.success && result.requires2FASetup) {
      return { success: false, requires2FASetup: true };
    }

    if ((result as any).code === 'FINGERPRINT_BANNED') {
      setUser(null);
      setToken(null);
      return {
        success: false,
        error: result.error || 'Sign-in is not allowed from this device',
        code: 'FINGERPRINT_BANNED',
      };
    }
    if ((result as any).code === 'ACCOUNT_BANNED') {
      setUser(null);
      setToken(null);
      return {
        success: false,
        error: result.error || 'Account is banned',
        code: 'ACCOUNT_BANNED',
        bannedUntil: (result as any).bannedUntil,
        bannedReason: (result as any).bannedReason,
      };
    }

    return { success: false, error: result.error || 'Google sign-in failed' };
  };

  const verify2FA = async (challengeToken: string, code: string) => {
    const result = await verify2FAApi(challengeToken, code);

    if (result.success && result.user) {
      setUser(result.user);
      if (result.token) {
        setToken(result.token);
      }
      return { success: true };
    }

    return { success: false, error: result.error || 'Invalid verification code' };
  };

  const signup = async (email: string, displayName: string, password: string) => {
    const result = await signupApi(email, displayName, password);
    
    if (result.success && result.user) {
      setUser(result.user);
      if (result.token) {
        setToken(result.token);
      }
      // Return the full result including user data and message
      return { 
        success: true, 
        user: result.user,
        message: (result as any).message 
      };
    }
    
    // Check for fingerprint banned error (403 status)
    if ((result as any).code === 'FINGERPRINT_BANNED') {
      return { 
        success: false, 
        error: result.error || 'Account creation is not allowed from this device',
        code: 'FINGERPRINT_BANNED'
      };
    }
    
    return { success: false, error: result.error || 'Signup failed' };
  };

  const logout = async () => {
    await logoutApi();
    setUser(null);
    setToken(null);
  };

  const refreshUser = async () => {
    await loadUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, googleLogin, verify2FA, signup, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

